import {NavLink, ufn} from '@edifecs/fp-ui-lib';
import {Routes} from '@angular/router';
import {Injectable} from '@angular/core';
import {RoutesProvider} from '@edifecs/fp-ui-shell';

/**
 * Provides app routes.
 */
@Injectable()
export class RoutesProviderService implements RoutesProvider {
    /**
     * Test whether the given nav link, or any of its descendants, has given path at its root
     * @param link Nav link
     * @param path Root path
     * @private
     */
    private hasRootPath(link: NavLink, path: string): boolean {
        if (link == null || !ufn.isText(path)) {
            return false;
        }

        if (ufn.isText(link.path) && link.path.split('/').includes(path)) {
            return true;
        }

        if (ufn.isArray(link.subItems) && link.subItems.length > 0) {
            return link.subItems.some(n => this.hasRootPath(n, path));
        }

        return false;
    }

    /**
     * Find the nav link that is marked as home
     * @param navLink
     * @param prefix path prefix
     * @private
     */
    private getHomeRoute(navLink: NavLink[], prefix?: string): string | null {
        for (const n of navLink) {
            if (n.isHome && ufn.isText(n.path)) {
                return ufn.isText(prefix) ? `${prefix}.${n.path}` : n.path;
            } else if (n.subItems instanceof Array) {
                return this.getHomeRoute(n.subItems, n.path);
            }
        }
        return null;
    }

    /**
     * @internal
     * @inheritDoc
     */
    async getAppRoutes(navLinks: NavLink[]): Promise<Routes> {
        const routes: Routes = [];

        // members
        if (navLinks.some(n => this.hasRootPath(n, 'members'))) {
            routes.push({
                path: 'members',
                loadChildren: () => import('@app/member/MemberModule').then(m => m.MemberModule)
            });
        }

        // practitioners
        if (navLinks.some(n => this.hasRootPath(n, 'practitioners'))) {
            routes.push({
                path: 'practitioners',
                loadChildren: () => import('@app/practitioner/PractitionerModule').then(m => m.PractitionerModule)
            });
        }

        // organizations
        if (navLinks.some(n => this.hasRootPath(n, 'organizations'))) {
            routes.push({
                path: 'organizations',
                loadChildren: () => import('@app/organization/OrganizationModule').then(m => m.OrganizationModule)
            });
        }

        // insurance plans
        if (navLinks.some(n => this.hasRootPath(n, 'insurance-plans'))) {
            routes.push({
                path: 'insurance-plans',
                loadChildren: () => import('@app/insurance-plan/InsurancePlanModule').then(m => m.InsurancePlanModule)
            });
        }

        // drug-formulary
        if (navLinks.some(n => this.hasRootPath(n, 'drug-formulary'))) {
            routes.push({
                path: 'drug-formulary',
                loadChildren: () => import('@app/formulary/DrugFormularyModule').then(m => m.DrugFormularyModule)
            });
        }

        // server
        if (navLinks.some(n => this.hasRootPath(n, 'server'))) {
            routes.push({
                path: 'server',
                loadChildren: () => import('@app/server/ServerQueryModule').then(m => m.ServerQueryModule)
            });
        }

        // retrieve-data
        if (navLinks.some(n => this.hasRootPath(n, 'retrieve-data'))) {
            routes.push({
                path: 'retrieve-data',
                loadChildren: () => import('@app/retrieve-data/DataRetrieveModule').then(m => m.DataRetrieveModule)
            });
        }

        // reset-data
        if (navLinks.some(n => this.hasRootPath(n, 'reset-data'))) {
            routes.push({
                path: 'reset-data',
                loadChildren: () => import('@app/reset-data/ResetDataModule').then(m => m.ResetDataModule)
            });
        }

        const homeRoute = this.getHomeRoute(navLinks);
        if (ufn.isText(homeRoute)) {
            const redirectTo = homeRoute.startsWith('/') ? homeRoute.slice(1) : homeRoute;
            routes.push({path: '', redirectTo});
        }
        return routes;
    }
}
